import { Typography, useTheme } from '@material-ui/core';
import { useFormik } from 'formik';
import React, { useRef } from 'react';
import moment from 'moment';
import AutoDebitCheck from '../../../components/Alerts/AutoDebitCheck';
import CancelObligationRestriction from '../../../components/Alerts/CancelObligationRestriction';
import ConditionsAgreement from '../../../components/Alerts/ConditionsAgreement';
import NegotiationStateAlert from '../../../components/Alerts/NegotiationStateAlert';
import UserConfirmationObligationAlert from '../../../components/Alerts/UserConfirmationObligationAlert';
import FormikDayPicker from '../../../components/FormikInputWrappers/FormikDayPicker';
import FormikDatePicker from '../../../components/FormikInputWrappers/FormikDatePicker';
import FormikSelectInput from '../../../components/FormikInputWrappers/FormikSelectInput';
import ErrorModal from '../../../components/Modal/ErrorModal';
import { useSpace } from '../../../content/utils/useSpace';
import RenderText from '../../../dictionaries/RenderText';
import { BrandType, NegotiationOption, NegotiationOptionAlternative, NegotiationOptionExtraConfirmationInfoType, OutDebt } from '../../../generated/graphql';
import { getNoPaymentReasonOptions } from '../../../utils/getNoPaymentReasonOptions';
import { isCypressTest } from '../../../isCypressTest';
import { CssSpreadType } from '../../../utils/css-spread-type';
import { getFormikValidator } from '../../../utils/getFormikValidator';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import IncomeOriginBasicForm, { IncomeFormRef } from './IncomeOriginBasicForm';
import PaymentCapacityForm from './PaymentCapacityForm';
import TermExtensionSoxForm, { TermExtensionSoxFormRef } from './TermExtensionSoxForm';
import { useGetMaxPaymentDateBySetting } from '../../../utils/useGetMaxPaymentDate';

interface INonPaymentReasonForm {
  onConfirm: (v: any) => void;
  outDebt: OutDebt;
  withCancel?: boolean;
  withUserConfirmation?: boolean;
  withNewPaymentDay?: boolean;
  withConditions?: boolean;
  withAutoDebit?: boolean;
  withPaymentCapacity?: boolean;
  withPaymentDate?: boolean;
  withTermExtensionSox?: boolean;
  withNoPaymentReason?: boolean;
  alternative?: NegotiationOptionAlternative;
  negotiation: NegotiationOption;
  outDebts?: OutDebt[];
}

const baseInitialValues = {};

const cancelInitialValues = {
  ...baseInitialValues,
  cancelCheckbox: false,
  newPaymentDay: '',
};

const acceptInitialValues = {
  ...baseInitialValues,
  acceptCheckbox: false,
};

const conditionInitialValues = {
  ...baseInitialValues,
  newTermCheck: false,
  newTermRateCheck: false,
  originalMonthlyRateCheck: false,
  restructuredCheck: false,
  age: null,
};

const autoDebitInitialValues = {
  ...baseInitialValues,
  autoDebitCheck: false,
};

const paymentCapacityInitialValues = {
  ...baseInitialValues,
  economic: '',
  declaredIncome: null,
  declaredPaymentCapacity: null,
};

const paymentDateInitialValues = {
  ...baseInitialValues,
  paymentDate: null as moment.Moment | null,
};

const noPaymentReasonInitialValues = {
  ...baseInitialValues,
  reason: '',
};

export type InitialValuesType = typeof baseInitialValues
  & Partial<typeof acceptInitialValues>
  & Partial<typeof cancelInitialValues>
  & Partial<typeof conditionInitialValues>
  & Partial<typeof autoDebitInitialValues>
  & Partial<typeof paymentCapacityInitialValues>
  & Partial<typeof paymentDateInitialValues>
  & Partial<typeof noPaymentReasonInitialValues>;

const baseInitialRules = {};

const cancelRules = {
  ...baseInitialRules,
  cancelCheckbox: 'accepted|required',
};

const acceptRules = {
  ...baseInitialRules,
  acceptCheckbox: 'accepted|required',
};

const conditionRules = {
  ...baseInitialRules,
  newTermCheck: 'accepted|required',
  newTermRateCheck: 'accepted|required',
  originalMonthlyRateCheck: 'accepted|required',
  restructuredCheck: 'accepted|required',
  age: 'required|integer|min:18|max:99',
};

const newPaymentDayRules = {
  ...baseInitialRules,
  newPaymentDay: 'required',
};

const autoDebitRules = {
  ...baseInitialRules,
  autoDebitCheck: 'accepted|required',
};

const paymentCapacityRules = {
  ...baseInitialRules,
  economic: 'required|not_in:desempleado',
  declaredIncome: 'required|numeric|min:1',
  declaredPaymentCapacity: 'required|numeric|min:1',
};

const paymentDateRules = {
  ...baseInitialRules,
  paymentDate: 'required',
};

const noPaymentReasonRules = {
  reason: 'required',
};

const attributeNames = {
  newPaymentDay: 'día de pago',
  paymentDate: 'fecha de pago',
};

const customMessages = {
  'accepted.cancelCheckbox': 'Debes aceptar la cancelación de tu obligación',
  'accepted.acceptCheckbox': 'Debes aceptar las condiciones',
  'required.newPaymentDay': 'Debes seleccionar un día de pago',
  'required.paymentDate': 'Debes seleccionar una fecha de pago',
  'required.reason': 'Debes ingresar el motivo de no pago',
  'accepted.newTermCheck': 'Debes aceptar los términos',
  'accepted.newTermRateCheck': 'Debes aceptar los términos',
  'accepted.originalMonthlyRateCheck': 'Debes aceptar los términos',
  'accepted.restructuredCheck': 'Debes aceptar los términos',
  'accepted.autoDebitCheck': 'Debes aceptar la autorización de débito automático',
  'required.age': 'Debes ingresar tu edad',
  'min.age': 'La edad no puede ser menor a 18',
  'max.age': 'La edad no puede ser mayor a 99',
  'min.declaredIncome': 'El ingreso no puede ser 0',
  'min.declaredPaymentCapacity': 'La capacidad de pago no puede ser 0',
  'required.economic': 'Debes seleccionar una actividad económica',
  'not_in.economic': 'Esta actividad económica no es válida',
  'required.declaredIncome': 'Debes ingresar tus ingresos mensuales',
  'numeric.declaredIncome': 'Tus ingresos mensuales deben ser un valor numérico',
  'required.declaredPaymentCapacity': 'Debes ingresar tu capacidad de pago mensual',
  'numeric.declaredPaymentCapacity': 'Tu capacidad de pago mensual debe ser un valor numérico',
};

const NonPaymentReasonForm: React.FC<INonPaymentReasonForm> = (props) => {
  const {
    onConfirm,
    children,
    withCancel,
    withConditions,
    withNewPaymentDay,
    withAutoDebit,
    withUserConfirmation,
    withPaymentCapacity,
    withPaymentDate,
    withTermExtensionSox,
    withNoPaymentReason,
    negotiation,
    outDebt,
    alternative,
    outDebts,
  } = props;

  const incomeFormRef = useRef<IncomeFormRef | null>(null);
  const termExtensionSoxFormRef = useRef<TermExtensionSoxFormRef | null>(null);
  const [ openModal, setOpenModal ] = React.useState(false);
  const theme = useTheme();

  const {
    isBogota,
    isScotiabank,
    isPaymentPromise,
    isPichincha,
    isCreditCard,
    isRefinance,
    isClaro,
    isLulo,
  } = getNegBooleans(outDebt, negotiation);
  const showNoPaymentReason = withNoPaymentReason;

  const { extraConfirmationInfo } = negotiation;
  const formRef = React.useRef<HTMLFormElement>(null);

  const getInitialValues = React.useCallback(() => {
    const updatedInitialValues = {
      ...baseInitialValues,
      ...(withCancel || withNewPaymentDay) && cancelInitialValues,
      ...(withConditions && conditionInitialValues),
      ...(withUserConfirmation && acceptInitialValues),
      ...(withAutoDebit && autoDebitInitialValues),
      ...(withPaymentCapacity && paymentCapacityInitialValues),
      ...(withPaymentDate && paymentDateInitialValues),
      ...(showNoPaymentReason && noPaymentReasonInitialValues),
    };

    return {
      ...updatedInitialValues,
      ...incomeFormRef.current?.initialValue,
      ...(withTermExtensionSox && termExtensionSoxFormRef.current?.initialValue),
    };
  }, [
    withCancel,
    withConditions,
    withNewPaymentDay,
    withUserConfirmation,
    withAutoDebit,
    withPaymentCapacity,
    withPaymentDate,
    withTermExtensionSox,
    showNoPaymentReason,
  ]);

  const getRules = React.useCallback(() => {
    const updatedRules = {
      ...baseInitialRules,
      ...(withCancel && cancelRules),
      ...(withNewPaymentDay && newPaymentDayRules),
      ...(withAutoDebit && autoDebitRules),
      ...(withConditions && conditionRules),
      ...(withUserConfirmation && acceptRules),
      ...(withPaymentCapacity && paymentCapacityRules),
      ...(withPaymentDate && paymentDateRules),
      ...(showNoPaymentReason && noPaymentReasonRules),
    };

    return {
      ...updatedRules,
      ...incomeFormRef.current?.validation.rules,
      ...(withTermExtensionSox && termExtensionSoxFormRef.current?.validation.rules),
    };
  }, [
    withCancel,
    withConditions,
    withNewPaymentDay,
    withUserConfirmation,
    withAutoDebit,
    withPaymentCapacity,
    withPaymentDate,
    withTermExtensionSox,
    showNoPaymentReason,
  ]);

  // Manage form state with localStorage persistence
  const [ formState, setFormState ] = React.useState(() => {
    const initialValues = getInitialValues();
    const rules = getRules();

    // Try to load saved values from localStorage
    const storageKey = `${outDebt.id}-${negotiation.type}`;
    const item = localStorage.getItem('formValues');
    const parsed: { [key: string]: InitialValuesType } = item ? JSON.parse(item) : {};
    const savedValues = parsed[storageKey];

    // If saved values exist, use them; otherwise, use initial calculated values
    const finalValues = savedValues || initialValues;

    // If no saved values were found, save the initial ones
    if (!savedValues) {
      localStorage.setItem('formValues', JSON.stringify({
        ...parsed,
        [storageKey]: initialValues,
      }));
    }

    return {
      initialValues: finalValues,
      rules,
    };
  });

  // Update initialValues and rules if dependencies change
  React.useEffect(() => {
    const initialValues = getInitialValues();
    const rules = getRules();

    setFormState((prevState) => ({
      initialValues: {
        ...initialValues,
        // Keep any previously saved values that are not in the new initialValues
        ...prevState.initialValues,
      },
      rules,
    }));
  }, [ getInitialValues, getRules ]);

  const formik = useFormik({
    initialValues: formState.initialValues,
    validate: getFormikValidator(formState.rules, attributeNames, undefined, {
      ...customMessages,
      ...incomeFormRef.current?.validation.messages,
      ...(withTermExtensionSox && termExtensionSoxFormRef.current?.validation.messages),
    }),
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (rawValues) => {
      // omit values that are not needed
      const { autoDebitCheck, ...values } = rawValues;
      const {
        newTermCheck,
        newTermRateCheck,
        originalMonthlyRateCheck,
        restructuredCheck,
        ...rest
      } = values;
      const transformedValues = {
        ...values,
      };
      onConfirm(withConditions ? rest : transformedValues);
    },
  });

  // Save in localStorage on value changes
  React.useEffect(() => {
    if (Object.keys(formik.values).length > 0) {
      const storageKey = `${outDebt.id}-${negotiation.type}`;
      const item = localStorage.getItem('formValues');
      const parsed: { [key: string]: InitialValuesType } = item ? JSON.parse(item) : {};

      localStorage.setItem('formValues', JSON.stringify({
        ...parsed,
        [storageKey]: formik.values,
      }));
    }
  }, [ formik.values, negotiation.type, outDebt.id ]);

  const onCloseModal = () => setOpenModal(false);

  const [ lastSubmitCount, setLastSubmitCount ] = React.useState(-1);

  React.useEffect(() => {
    const elementWithError = formRef?.current?.querySelector(`*[name=${Object.keys(formik.errors)[0]}]`);
    if (lastSubmitCount !== formik.submitCount) setLastSubmitCount(formik.submitCount);
    if (lastSubmitCount !== formik.submitCount && !formik.isValid) {
      if (!isCypressTest) {
        elementWithError?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
      }
      setOpenModal(true);
    }
  }, [ formik.submitCount, lastSubmitCount, formik.isValid, formik.errors ]);

  const economicActivity = extraConfirmationInfo
  === NegotiationOptionExtraConfirmationInfoType.EconomicActivity;

  const isPichinchaPaymentPromise = isPichincha && isPaymentPromise;
  const isPichinchaRefinance = isPichincha && isRefinance && isCreditCard;

  const spaceContext = {
    outDebt,
    option: negotiation,
    alternative,
  };
  const paymentConfirmationText = useSpace('NEG_OPTION_CONDITIONS', 'confirmation.text', spaceContext);
  const noShowIncomeOriginBasicForm = (withUserConfirmation && isBogota)
    || (isScotiabank && (isPaymentPromise || isRefinance)) || isClaro || isLulo;

  const brand = outDebt.brand || BrandType.Tranquiapp;
  const { maxPaymentDate } = useGetMaxPaymentDateBySetting(
    outDebt.bank,
    brand,
    negotiation,
  );

  if (!alternative) return null;

  return (
    <form onSubmit={formik.handleSubmit} ref={formRef}>
      <ErrorModal openModal={openModal} onClose={onCloseModal} />
      {withUserConfirmation && (
        <UserConfirmationObligationAlert
          negotiation={negotiation || undefined}
          formik={formik}
          alternative={alternative}
          outDebt={outDebt}
          outDebts={outDebts}
        />
      )}
      {withCancel && (
        <CancelObligationRestriction
          negotiation={negotiation || undefined}
          formik={formik}
          alternative={alternative}
          outDebt={outDebt}
        />
      )}
      {withConditions && (
        <ConditionsAgreement
          negotiation={negotiation || undefined}
          formik={formik}
          alternative={alternative}
          outDebt={outDebt}
        />
      )}
      {withPaymentDate && (
      <FormikDatePicker
        formik={formik}
        valueId="paymentDate"
        label={`Selecciona la fecha máxima del pago${alternative.term > 1 ? ' inicial' : ''}`}
        fullWidth
        className="mb-2"
        minDate={moment()}
        maxDate={maxPaymentDate || moment().endOf('month')}
        emptyLabel="Seleccionar fecha"
      />
      )}
      {withNewPaymentDay && (
        <FormikDayPicker
          formik={formik}
          valueId="newPaymentDay"
          label="Selecciona el día de pago para tus siguientes cuotas mensuales"
          fullWidth
          className="mb-2"
          emptyLabel="Seleccionar día"
        />
      )}
      {!economicActivity
      && (
      <NegotiationStateAlert
        className="mb-2"
        outDebt={outDebt}
        option={negotiation}
        alternative={alternative}
        extraInfo={{
          newPaymentDay: parseInt(formik.values.newPaymentDay || '', 10),
          paymentDate: formik.values.paymentDate ?? negotiation.settings?.maxPaymentDate,
        }}
        outDebts={outDebts}
        inSummary
      >
        {withAutoDebit && (
        <AutoDebitCheck
          negotiation={negotiation || undefined}
          formik={formik}
          outDebt={outDebt}
        />
        )}
      </NegotiationStateAlert>
      )}
      {(isPichinchaPaymentPromise || isPichinchaRefinance)
      && (
      <Typography
        variant="body2"
        className="mb-2"
        style={{ ...(theme.typography.body1 as CssSpreadType), color: theme.palette.indigo.main }}
      >
        <RenderText
          isMarkdown
          textKey={paymentConfirmationText.key}
          context={paymentConfirmationText.context}
        />
      </Typography>
      )}
      {!noShowIncomeOriginBasicForm && (
        <>
          <Typography
            variant="h5"
            className="mb-2"
            style={{
              ...(theme.typography.body1 as CssSpreadType),
              color: theme.palette.indigo.main,
              fontWeight: 600,
            }}
            data-test="cy-summaryFormGeneralTitle-text"
          >
            <RenderText
              isMarkdown
              textKey="summaryView_generalform_Title"
            />
          </Typography>
          <IncomeOriginBasicForm
            ref={incomeFormRef}
            formik={formik}
            confInfoType={negotiation.extraConfirmationInfo!}
            negOption={negotiation}
            outDebt={outDebt}
          />
        </>
      )}
      {withPaymentCapacity && (
        <PaymentCapacityForm
          formik={formik}
          outDebt={outDebt}
          alternative={alternative}
        />
      )}
      {withTermExtensionSox && (
        <TermExtensionSoxForm
          ref={termExtensionSoxFormRef}
          formik={formik}
        />
      )}
      {showNoPaymentReason && (
        <FormikSelectInput
          label="Motivo de no pago"
          valueId="reason"
          formik={formik}
          options={getNoPaymentReasonOptions(outDebt.bank, outDebt.brand)}
          displayEmpty
          placeholder="Selecciona un motivo"
          fullWidth
          className="mb-2"
          data-test="cy-noPaymentReason-select"
        />
      )}
      {children}
    </form>
  );
};

export default NonPaymentReasonForm;
